// Vendored from the JETCAM DXF Viewer (src/render/draw.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type Primitive } from "../geometry/index.ts";
import { type Vec3 } from "../parse/index.ts";
import { affineOf, type Camera, type Point, viewportBounds, worldToScreen } from "./camera.ts";
import { type Rgb, rgbToCss } from "./colors.ts";
import { type Scene } from "./scene.ts";
import { type IndexedPrimitive } from "./spatial.ts";

/**
 * The Canvas 2D renderer.
 *
 * Two rules shape everything here:
 *
 * 1. **One transform.** The camera's affine goes into `ctx.setTransform` and
 *    every draw call passes world coordinates straight through. Nothing
 *    transforms a point by hand, so the renderer, the ruler and the hit test
 *    cannot disagree about where anything is.
 * 2. **Arcs are arcs.** They go to `ctx.arc`, never to a pre-tessellated
 *    polyline. The whole point of keeping them true through `geometry/` is
 *    that they stay smooth at any zoom, and the browser rasterises them better
 *    and faster than we could feed it line segments.
 */

export interface RenderStyle {
  readonly background: Rgb;
  /** Stroke width in screen pixels, held constant at every zoom. */
  readonly lineWidthPx: number;
  /** Half-size of a POINT marker, in screen pixels. */
  readonly pointSizePx: number;
  readonly highlightColor: Rgb;
  readonly highlightWidthPx: number;
  /** Geometry a finding points at, drawn in its own colour on top. */
  readonly flagColor: Rgb;
  /** Compare mode: a shape only file B has. */
  readonly diffAddedColor: Rgb;
  /** Compare mode: a shape only file A has. */
  readonly diffRemovedColor: Rgb;
  /** Compare mode: a shape present in both files but not the same. */
  readonly diffChangedColor: Rgb;
  /** Small markers showing where every finding is, at once. */
  readonly markerColor: Rgb;
  readonly markerSizePx: number;
  /** Text smaller than this many pixels is not drawn. */
  readonly minTextPx: number;
  readonly fontFamily: string;
}

export const DARK_STYLE: RenderStyle = {
  background: { r: 30, g: 30, b: 30 },
  lineWidthPx: 1,
  pointSizePx: 3,
  highlightColor: { r: 255, g: 170, b: 0 },
  highlightWidthPx: 3,
  flagColor: { r: 255, g: 76, b: 76 },
  diffAddedColor: { r: 70, g: 210, b: 110 },
  diffRemovedColor: { r: 235, g: 70, b: 70 },
  diffChangedColor: { r: 255, g: 205, b: 60 },
  markerColor: { r: 255, g: 76, b: 76 },
  markerSizePx: 4,
  minTextPx: 5,
  fontFamily: 'ui-sans-serif, system-ui, "Segoe UI", Arial, sans-serif',
};

export const LIGHT_STYLE: RenderStyle = {
  ...DARK_STYLE,
  background: { r: 250, g: 250, b: 250 },
  highlightColor: { r: 210, g: 90, b: 0 },
  flagColor: { r: 200, g: 0, b: 0 },
  diffAddedColor: { r: 20, g: 140, b: 60 },
  diffRemovedColor: { r: 190, g: 20, b: 20 },
  diffChangedColor: { r: 175, g: 125, b: 0 },
  markerColor: { r: 200, g: 0, b: 0 },
};

/** Compare mode's added/removed/changed primitive ids for one scene. Any set left empty is simply not drawn. */
export interface DiffOverlay {
  readonly added: ReadonlySet<number>;
  readonly removed: ReadonlySet<number>;
  readonly changed: ReadonlySet<number>;
}

export interface RenderInput {
  readonly ctx: CanvasRenderingContext2D;
  readonly camera: Camera;
  readonly scene: Scene;
  readonly style: RenderStyle;
  readonly devicePixelRatio: number;
  /** Ids of primitives to draw highlighted, on top. */
  readonly highlighted?: ReadonlySet<number>;
  /** Ids of primitives a finding points at, drawn in the flag colour. */
  readonly flagged?: ReadonlySet<number>;
  /** World positions to mark, so every finding is visible at once. */
  readonly markers?: readonly Vec3[];
  /** Compare mode's diff coloring for this scene. */
  readonly diffOverlay?: DiffOverlay;
  /**
   * Whether to clear the canvas before drawing. Default `true`. Compare
   * mode's Overlay view sets this `false` for its second `renderScene()`
   * pass, so file B's geometry draws on top of file A's instead of erasing it.
   */
  readonly clear?: boolean;
}

export interface RenderStats {
  /** Primitives the index returned for this viewport. */
  readonly candidates: number;
  /** Primitives actually stroked. */
  readonly drawn: number;
  readonly textDrawn: number;
  /** stroke() calls issued for geometry. See SUBPATHS_PER_STROKE. */
  readonly batches: number;
  /** Distinct resolved colours in this frame. */
  readonly colorGroups: number;
}

/**
 * How many subpaths to accumulate before stroking.
 *
 * This number is worth more than it looks. Building one path per colour and
 * stroking it once is the obvious way to batch, and on a 50,000-primitive nest
 * it takes **716 ms a frame**. The same primitives stroked in chunks of ~128
 * take **6 ms** — a 100x difference, measured in Chrome, with everything else
 * identical.
 *
 * The cause is the path itself, not the pixels: Skia's handling degrades
 * sharply once a single path holds tens of thousands of subpaths. Line cap and
 * join settings, which are the usual suspects, made no measurable difference
 * either way.
 *
 * Between roughly 50 and 200 the curve is flat, so the exact value is not
 * delicate; below that, per-stroke overhead starts to show.
 */
const SUBPATHS_PER_STROKE = 128;

/** Reset to device pixels: used for clearing and for screen-space text. */
function setScreenTransform(ctx: CanvasRenderingContext2D, dpr: number): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/**
 * Add one primitive to the current path.
 *
 * `ctx.arc` continues the existing subpath with a straight line unless a
 * `moveTo` precedes it, which is why every arc starts with one. Batching a
 * whole colour group into a single path and stroking once is the difference
 * between one `stroke()` per frame per colour and one per primitive.
 *
 * `relativeTo` is always `camera.center` (see the call sites) — every
 * coordinate has it subtracted, in JS double precision, before reaching the
 * canvas. See `affineOf`'s own doc comment for why: this is the other half
 * of the same fix, and unconditional in exactly the same way.
 */
function addToPath(ctx: CanvasRenderingContext2D, p: Primitive, relativeTo: Point): boolean {
  switch (p.kind) {
    case "line":
      ctx.moveTo(p.a.x - relativeTo.x, p.a.y - relativeTo.y);
      ctx.lineTo(p.b.x - relativeTo.x, p.b.y - relativeTo.y);
      return true;

    case "polyline": {
      const pts = p.points;
      if (pts.length < 2) return false;
      ctx.moveTo(pts[0]!.x - relativeTo.x, pts[0]!.y - relativeTo.y);
      for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i]!.x - relativeTo.x, pts[i]!.y - relativeTo.y);
      if (p.closed) ctx.closePath();
      return true;
    }

    case "arc": {
      if (!(p.radius > 0)) return false;
      const cx = p.center.x - relativeTo.x;
      const cy = p.center.y - relativeTo.y;
      ctx.moveTo(cx + p.radius * Math.cos(p.startAngle), cy + p.radius * Math.sin(p.startAngle));
      // Our `ccw` means "the parameter increases". Canvas's flag is the
      // opposite sense, and is unaffected by the Y-flip in the transform
      // because it is expressed in the current user space.
      ctx.arc(cx, cy, p.radius, p.startAngle, p.endAngle, !p.ccw);
      return true;
    }

    default:
      return false;
  }
}

/** POINT markers: a fixed-size cross in screen space, so they stay findable. */
function addPointMarker(ctx: CanvasRenderingContext2D, p: Primitive, half: number, relativeTo: Point): boolean {
  if (p.kind !== "point") return false;
  const x = p.position.x - relativeTo.x;
  const y = p.position.y - relativeTo.y;
  ctx.moveTo(x - half, y);
  ctx.lineTo(x + half, y);
  ctx.moveTo(x, y - half);
  ctx.lineTo(x, y + half);
  return true;
}

export function renderScene(input: RenderInput): RenderStats {
  const { ctx, camera, style, devicePixelRatio: dpr } = input;

  setScreenTransform(ctx, dpr);
  if (input.clear ?? true) {
    ctx.fillStyle = rgbToCss(style.background);
    ctx.fillRect(0, 0, camera.width, camera.height);
  }

  const a = affineOf(camera);
  ctx.setTransform(dpr * a.a, dpr * a.b, dpr * a.c, dpr * a.d, dpr * a.e, dpr * a.f);

  // Constant in screen space: the transform scales it back down, so a hairline
  // stays a hairline whether the part is 2mm or 2m across.
  const worldLineWidth = style.lineWidthPx / camera.scale;
  const pointHalf = style.pointSizePx / camera.scale;
  ctx.lineWidth = worldLineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const scene = input.scene;
  const candidates = scene.index.search(viewportBounds(camera));

  // Bucket by style slot (colour + linetype together, since setLineDash is
  // canvas state exactly like strokeStyle). The slot is a small integer
  // worked out once when the scene was built, so this loop does no Map
  // lookups, allocates no strings, and reuses its buckets across frames.
  //
  // A POINT primitive sharing a bucket with a dashed/dotted linetype can have
  // its tiny cross-hair marker fall entirely in a dash gap, since the whole
  // bucket shares one beginPath()/stroke() and dash phase carries across
  // subpaths. Accepted: POINT entities are essentially always CONTINUOUS in
  // practice, and splitting them into their own bucket would cost real
  // complexity for a case that doesn't happen.
  const buckets = scene.takeStyleBuckets();
  const texts: IndexedPrimitive[] = [];
  let drawn = 0;
  let usedGroups = 0;

  for (const entry of candidates) {
    if (!scene.isVisible(entry.id)) continue;
    if (entry.primitive.kind === "text") {
      texts.push(entry);
      continue;
    }
    const bucket = buckets[scene.styleSlot[entry.id] ?? 0];
    if (bucket) bucket.push(entry);
  }

  let batches = 0;
  for (let slot = 0; slot < buckets.length; slot += 1) {
    const entries = buckets[slot]!;
    if (entries.length === 0) continue;
    usedGroups += 1;

    const style = scene.stylePalette[slot]!;
    ctx.strokeStyle = style.color;
    ctx.setLineDash(style.dash as number[]);
    ctx.beginPath();
    let inPath = 0;
    for (const entry of entries) {
      const p = entry.primitive;
      const added = p.kind === "point" ? addPointMarker(ctx, p, pointHalf, camera.center) : addToPath(ctx, p, camera.center);
      if (!added) continue;
      drawn += 1;
      inPath += 1;
      if (inPath >= SUBPATHS_PER_STROKE) {
        ctx.stroke();
        ctx.beginPath();
        batches += 1;
        inPath = 0;
      }
    }
    if (inPath > 0) {
      ctx.stroke();
      batches += 1;
    }
  }

  const textDrawn = drawText(input, texts, dpr);

  // Flagged geometry first, so a hovered flagged primitive still reads as
  // hovered rather than being painted over by its finding colour.
  if (input.flagged && input.flagged.size > 0) {
    drawOverlayPaths(input, dpr, input.flagged, style.flagColor, style.highlightWidthPx);
  }
  if (input.highlighted && input.highlighted.size > 0) {
    drawOverlayPaths(input, dpr, input.highlighted, style.highlightColor, style.highlightWidthPx);
  }
  if (input.markers && input.markers.length > 0) {
    drawMarkers(input, dpr);
  }
  // Changed drawn before added/removed: on a changed shape, "this differs" is
  // the more specific and more useful thing to see than "this side has it".
  if (input.diffOverlay) {
    const { added, removed, changed } = input.diffOverlay;
    if (changed.size > 0) drawOverlayPaths(input, dpr, changed, style.diffChangedColor, style.highlightWidthPx);
    if (removed.size > 0) drawOverlayPaths(input, dpr, removed, style.diffRemovedColor, style.highlightWidthPx);
    if (added.size > 0) drawOverlayPaths(input, dpr, added, style.diffAddedColor, style.highlightWidthPx);
  }

  return { candidates: candidates.length, drawn, textDrawn, batches, colorGroups: usedGroups };
}

/**
 * Text is drawn in screen space, not world space.
 *
 * Under the world transform the vertical flip would render every label upside
 * down, and a font size expressed in drawing units goes to absurd values at
 * either end of the zoom range. Converting to screen coordinates and drawing
 * with a pixel font size avoids both, and comes out crisp.
 */
function drawText(input: RenderInput, texts: IndexedPrimitive[], dpr: number): number {
  if (texts.length === 0) return 0;
  const { ctx, camera, style } = input;

  setScreenTransform(ctx, dpr);
  ctx.textBaseline = "alphabetic";
  let count = 0;

  for (const entry of texts) {
    const p = entry.primitive;
    if (p.kind !== "text") continue;

    const px = p.height * camera.scale;
    // Below a few pixels a label is an illegible smudge that still costs a
    // full text rasterisation, and a busy drawing has thousands of them.
    if (px < style.minTextPx) continue;

    const screen = worldToScreen(camera, p.position);
    ctx.save();
    ctx.translate(screen.x, screen.y);
    // Screen Y runs down, so a counter-clockwise world rotation is a clockwise
    // screen rotation.
    ctx.rotate(-p.rotation);
    if (p.mirrored) ctx.scale(-1, 1);
    ctx.font = `${px}px ${style.fontFamily}`;
    ctx.fillStyle = input.scene.palette[input.scene.colorSlot[entry.id] ?? 0] ?? "#fff";
    ctx.fillText(p.text, 0, 0);
    ctx.restore();
    count += 1;
  }

  return count;
}

function drawOverlayPaths(
  input: RenderInput,
  dpr: number,
  ids: ReadonlySet<number>,
  color: Rgb,
  widthPx: number,
): void {
  const { ctx, camera, style } = input;
  const index = input.scene.index;

  const a = affineOf(camera);
  ctx.setTransform(dpr * a.a, dpr * a.b, dpr * a.c, dpr * a.d, dpr * a.e, dpr * a.f);
  // A highlight/flag overlay always outlines solid, regardless of the
  // underlying entity's own linetype or whatever the geometry loop's last
  // style bucket left set — setLineDash is canvas state that otherwise leaks
  // across draw calls.
  ctx.setLineDash([]);
  ctx.lineWidth = widthPx / camera.scale;
  ctx.strokeStyle = rgbToCss(color);
  ctx.beginPath();

  for (const id of ids) {
    const entry = index.entries[id];
    if (!entry) continue;
    const p = entry.primitive;
    if (p.kind === "point") addPointMarker(ctx, p, style.pointSizePx / camera.scale, camera.center);
    else if (p.kind === "text") {
      // Box the text rather than restating it in the highlight colour, rotated
      // (and mirrored) the same way the glyph itself is drawn (see drawText)
      // — otherwise the box comes out axis-aligned for any rotated label,
      // never matching what is actually on screen.
      const r = Math.max(p.height, p.text.length * p.height * 0.75);
      const bottom = -p.height * 0.25;
      const top = p.height * 1.25;
      const width = p.mirrored ? -r : r;
      const cos = Math.cos(p.rotation);
      const sin = Math.sin(p.rotation);
      const cx = p.position.x - camera.center.x;
      const cy = p.position.y - camera.center.y;
      const corners: readonly [number, number][] = [
        [0, bottom],
        [width, bottom],
        [width, top],
        [0, top],
      ];
      corners.forEach(([lx, ly], i) => {
        const wx = cx + lx * cos - ly * sin;
        const wy = cy + lx * sin + ly * cos;
        if (i === 0) ctx.moveTo(wx, wy);
        else ctx.lineTo(wx, wy);
      });
      ctx.closePath();
    } else addToPath(ctx, p, camera.center);
  }

  ctx.stroke();
}

/**
 * A small ring at every finding's location, drawn in screen space so the marks
 * stay the same size at any zoom.
 *
 * The point is to see the whole distribution at a glance — twelve gaps all
 * down one edge is a different story from twelve scattered at random.
 */
function drawMarkers(input: RenderInput, dpr: number): void {
  const { ctx, camera, style, markers } = input;
  if (!markers) return;

  setScreenTransform(ctx, dpr);
  ctx.setLineDash([]); // same leak-prevention as drawOverlayPaths
  ctx.strokeStyle = rgbToCss(style.markerColor);
  ctx.lineWidth = 1.5;
  ctx.beginPath();

  const r = style.markerSizePx;
  for (const m of markers) {
    const s = worldToScreen(camera, m);
    // Off-screen marks cost a full arc each on a drawing with thousands.
    if (s.x < -r || s.y < -r || s.x > camera.width + r || s.y > camera.height + r) continue;
    ctx.moveTo(s.x + r, s.y);
    ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
  }
  ctx.stroke();
}
